import React, { useEffect, useRef } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { LayoutGrid, Box, Layers, Boxes, User, LogOut, Shield } from "lucide-react";
import KeyChip from "../primitives/KeyChip";
import IconButton from "../primitives/IconButton";
import "./LeftMenu.css";

const NAV_ITEMS = [
    { to: "/dashboard", label: "Dashboard", icon: LayoutGrid, chord: "G + D" },
    { to: "/visualizer", label: "Visualizer (single)", icon: Box, chord: "G + V" },
    { to: "/manifest", label: "Visualizer (manifest)", icon: Layers, chord: "G + M" },
    { to: "/catalog", label: "Catalog", icon: Boxes, chord: "G + C" },
    { to: "/account", label: "Account", icon: User, chord: "G + A" },
];

const ADMIN_NAV_ITEM = {
    to: "/admin",
    label: "Admin",
    icon: Shield,
    chord: "G + Z",
    admin: true,
};

function useChordNav(map) {
    const navigate = useNavigate();
    const pending = useRef(null);
    const timer = useRef(null);

    useEffect(() => {
        const reset = () => {
            pending.current = null;
            if (timer.current) {
                clearTimeout(timer.current);
                timer.current = null;
            }
        };
        const onKey = (e) => {
            const t = e.target;
            if (
                t &&
                (t.tagName === "INPUT" ||
                    t.tagName === "TEXTAREA" ||
                    t.tagName === "SELECT" ||
                    t.isContentEditable)
            ) {
                return;
            }
            if (e.metaKey || e.ctrlKey || e.altKey) return;
            const k = e.key.toLowerCase();
            if (pending.current === "g") {
                const dest = map[k];
                if (dest) {
                    e.preventDefault();
                    navigate(dest);
                }
                reset();
                return;
            }
            if (k === "g") {
                pending.current = "g";
                if (timer.current) clearTimeout(timer.current);
                timer.current = setTimeout(reset, 1200);
            }
        };
        window.addEventListener("keydown", onKey);
        return () => {
            window.removeEventListener("keydown", onKey);
            reset();
        };
    }, [map, navigate]);
}

function LeftMenu({ user, onSignOut }) {
    const location = useLocation();
    const isAdmin = !!user?.is_admin;
    useChordNav({
        d: "/dashboard",
        v: "/visualizer",
        m: "/manifest",
        c: "/catalog",
        a: "/account",
        ...(isAdmin ? { z: "/admin" } : {}),
    });

    const initial = (user?.name || user?.displayName || user?.email || "?")
        .charAt(0)
        .toUpperCase();

    return (
        <aside className="left-menu" aria-label="Primary navigation">
            <div className="left-menu__brand">
                <span className="left-menu__mark" aria-hidden="true">
                    <svg viewBox="0 0 16 16" width="14" height="14">
                        <rect
                            x="1"
                            y="1"
                            width="14"
                            height="14"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="1.25"
                        />
                        <rect x="4.5" y="4.5" width="7" height="7" fill="currentColor" />
                    </svg>
                </span>
                <span className="left-menu__brand-name">PACKERAI</span>
                <span className="left-menu__brand-sub">v1.0</span>
            </div>

            <nav className="left-menu__nav">
                <div className="left-menu__nav-label uppercase-label">Navigate</div>
                {NAV_ITEMS.map((item) => {
                    const active = location.pathname === item.to;
                    const Icon = item.icon;
                    return (
                        <Link
                            key={item.to}
                            to={item.to}
                            className={`left-menu__link focus-ring ${active ? "is-active" : ""}`}
                            aria-current={active ? "page" : undefined}
                            title={`${item.label} (${item.chord})`}
                        >
                            <Icon size={14} aria-hidden="true" />
                            <span className="left-menu__link-label">{item.label}</span>
                            <KeyChip className="left-menu__link-chord">{item.chord}</KeyChip>
                        </Link>
                    );
                })}

                {isAdmin && (() => {
                    const item = ADMIN_NAV_ITEM;
                    const active = location.pathname.startsWith("/admin");
                    const Icon = item.icon;
                    return (
                        <>
                            <div className="left-menu__nav-divider" aria-hidden="true" />
                            <Link
                                to={item.to}
                                className={`left-menu__link focus-ring ${active ? "is-active" : ""}`}
                                aria-current={active ? "page" : undefined}
                                title={`${item.label} (${item.chord})`}
                            >
                                <Icon size={14} aria-hidden="true" />
                                <span className="left-menu__link-label">{item.label}</span>
                                <KeyChip className="left-menu__link-chord">{item.chord}</KeyChip>
                            </Link>
                        </>
                    );
                })()}
            </nav>

            <div className="left-menu__footer">
                <div className="left-menu__user">
                    {user?.picture ? (
                        <img
                            src={user.picture}
                            alt=""
                            className="left-menu__avatar"
                            referrerPolicy="no-referrer"
                        />
                    ) : (
                        <div
                            className="left-menu__avatar left-menu__avatar--fallback"
                            aria-hidden="true"
                        >
                            {initial}
                        </div>
                    )}
                    <div className="left-menu__user-text">
                        <div className="left-menu__user-name">
                            {user?.name || user?.displayName || "Operator"}
                        </div>
                        <div className="left-menu__user-email" title={user?.email || ""}>
                            {user?.email || ""}
                        </div>
                    </div>
                    <IconButton
                        icon={LogOut}
                        label="Sign out"
                        tone="danger"
                        size="sm"
                        onClick={onSignOut}
                    />
                </div>
            </div>
        </aside>
    );
}

export default LeftMenu;
